// src/components/Header.js
import React, { useState, useRef, useEffect, useCallback } from 'react';

const DefaultProfileIcon = ({ size = 32, color = '#555' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    xmlns="http://www.w3.org/2000/svg"
    style={{ borderRadius: '50%', backgroundColor: '#ddd' }}
  >
    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
  </svg>
);

export default function Header({ user, onSignOut }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef();

  // Close dropdown on outside click
  const handleClickOutside = useCallback(
    e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    },
    [setDropdownOpen]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const toggleDropdown = () => setDropdownOpen(open => !open);
  const handleSearchChange = e => setQuery(e.target.value);

  return (
    <header
      className="header"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        backgroundColor: '#4682B4',
        color: '#e2e8f0',
        boxShadow: '0 2px 6px rgb(0 0 0 / 0.3)',
      }}
    >
      {/* Left: Logo + Title */}
      <div
        className="logo"
        style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}
      >
        <a href="/" style={{ display: 'inline-block', cursor: 'pointer' }}>
          <img src="./logo.png" alt="Company Logo" height="40" />
        </a>
        <span
          style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#e2e8f0',
            userSelect: 'none',
          }}
        >
          CloudFactory SFTP
        </span>
      </div>

      {/* Center: Search Bar */}
      <div
        className="search-container"
        style={{
          flex: '1 1 auto',             // take up remaining space :contentReference[oaicite:0]{index=0}
          display: 'flex',
          justifyContent: 'center',     // center horizontally :contentReference[oaicite:1]{index=1}
        }}
      >
        <input
          type="search"
          value={query}
          onChange={handleSearchChange}
          placeholder="Search…"
          style={{
            width: '100%',              // full width of this flex cell :contentReference[oaicite:2]{index=2}
            maxWidth: 400,
            padding: '6px 12px',
            borderRadius: 4,
            border: '1px solid #ccc',
            fontSize: '1rem',
          }}
        />
      </div>

      {/* Right: User Profile / Dropdown */}
      <div
        className="profile"
        ref={dropdownRef}
        style={{ position: 'relative', cursor: 'pointer', userSelect: 'none', flex: '0 0 auto' }}
      >
        <button
          onClick={toggleDropdown}
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
          aria-label="User menu"
          type="button"
          style={{
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'inherit',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            padding: 4,
            borderRadius: 6,
          }}
          onMouseEnter={e =>
            (e.currentTarget.style.backgroundColor = 'rgba(255 255 255 / 0.1)')
          }
          onMouseLeave={e =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
        >
          {user?.attributes?.picture ? (
            <img
              src={user.attributes.picture}
              alt="Profile"
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          ) : (
            <DefaultProfileIcon size={32} color="#a0aec0" />
          )}
          <span>Welcome, {user?.username || 'User'}</span>
          <svg
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            style={{ marginLeft: 4, flexShrink: 0 }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {dropdownOpen && (
          <ul
            role="menu"
            aria-label="User menu"
            style={{
              position: 'absolute',
              right: 0,
              marginTop: 8,
              backgroundColor: '#2d3748',
              boxShadow: '0 4px 12px rgb(0 0 0 / 0.4)',
              borderRadius: 6,
              listStyle: 'none',
              padding: '8px 0',
              minWidth: 140,
              zIndex: 1000,
            }}
          >
            <li
              role="menuitem"
              tabIndex={0}
              onClick={() => {
                setDropdownOpen(false);
                onSignOut();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDropdownOpen(false);
                  onSignOut();
                }
              }}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                color: '#e2e8f0',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.backgroundColor = '#4a5568')
              }
              onMouseLeave={e =>
                (e.currentTarget.style.backgroundColor = 'transparent')
              }
            >
              Sign Out
            </li>
          </ul>
        )}
      </div>
    </header>
  );
}
